import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import RecoveryLayout from '../../components/RecoveryLayout';
import PageLoader from '../../components/PageLoader';
import axiosInstance from '../../utils/axiosInstance';

const STATUS_OPTIONS = ['all', 'pending', 'partial', 'paid', 'waived'];

const STATUS_COLORS = {
  pending: { bg: '#fee2e2', color: '#991b1b' },
  partial: { bg: '#fef3c7', color: '#92400e' },
  paid:    { bg: '#dcfce7', color: '#166534' },
  waived:  { bg: '#e0f2fe', color: '#0c4a6e' },
};

export default function Arrears({ pageTitle, section }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const resp = await axiosInstance.get('/admin/recovery-arrears');
      setRecords(resp?.data?.arrears ?? []);
    } catch {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRecords(); }, []);

  const filtered = records.filter((r) => {
    const matchSearch =
      !search ||
      (r.studentName || '').toLowerCase().includes(search.toLowerCase()) ||
      (r.studentNumber || '').toLowerCase().includes(search.toLowerCase()) ||
      (r.course || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || r.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleMarkPaid = async (arrearsId) => {
    const result = await Swal.fire({
      title: 'Mark as Paid?',
      text: 'This will mark this arrears record as fully paid.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#16a34a',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, mark paid',
    });

    if (!result.isConfirmed) return;

    try {
      await axiosInstance.patch(`/admin/recovery-arrears/${arrearsId}/status`, { status: 'paid' });
      Swal.fire({ icon: 'success', title: 'Marked as Paid', timer: 1500, showConfirmButton: false });
      fetchRecords();
    } catch {
      Swal.fire({ icon: 'error', title: 'Failed', text: 'Could not update status.' });
    }
  };

  const fmt = (n) =>
    typeof n === 'number' ? `Rs ${new Intl.NumberFormat('en-US').format(n)}` : '—';

  if (loading) return <RecoveryLayout title={pageTitle || 'Arrears'} currentSection={section}><PageLoader /></RecoveryLayout>;

  return (
    <RecoveryLayout title={pageTitle || 'Arrears'} currentSection={section}>
      {/* Filters */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 24,
          background: '#fff',
          padding: '16px 20px',
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
        }}
      >
        <input
          type="text"
          placeholder="Search by name, ID or course…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          style={{
            flex: 1,
            minWidth: 220,
            padding: '8px 12px',
            borderRadius: 8,
            border: '1px solid #d1d5db',
            fontSize: 14,
            outline: 'none',
          }}
        />
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            border: '1px solid #d1d5db',
            fontSize: 14,
            background: '#fff',
            cursor: 'pointer',
          }}
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
        <span style={{ fontSize: 13, color: '#64748b' }}>{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                {['Student', 'Student No.', 'Course', 'Batch', 'Total Fee', 'Paid', 'Outstanding', 'Status', 'Actions'].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: '12px 16px',
                      color: '#64748b',
                      fontWeight: 600,
                      borderBottom: '1px solid #e2e8f0',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: '32px 16px', textAlign: 'center', color: '#94a3b8' }}>
                    No arrears records found.
                  </td>
                </tr>
              ) : (
                paginated.map((row, i) => {
                  const sc = STATUS_COLORS[row.status] || { bg: '#f3f4f6', color: '#1f2937' };
                  return (
                    <tr key={row.arrearsId || i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 16px', color: '#0f172a', fontWeight: 500 }}>{row.studentName || '—'}</td>
                      <td style={{ padding: '10px 16px', color: '#64748b' }}>{row.studentNumber || '—'}</td>
                      <td style={{ padding: '10px 16px', color: '#64748b' }}>{row.course || '—'}</td>
                      <td style={{ padding: '10px 16px', color: '#64748b' }}>{row.batch || '—'}</td>
                      <td style={{ padding: '10px 16px', color: '#0f172a' }}>{fmt(row.totalFee)}</td>
                      <td style={{ padding: '10px 16px', color: '#16a34a' }}>{fmt(row.paidAmount)}</td>
                      <td style={{ padding: '10px 16px', color: '#dc2626', fontWeight: 600 }}>{fmt(row.outstanding)}</td>
                      <td style={{ padding: '10px 16px' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '3px 10px',
                            borderRadius: 6,
                            fontSize: 11,
                            fontWeight: 600,
                            background: sc.bg,
                            color: sc.color,
                          }}
                        >
                          {row.status || 'pending'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        {row.status !== 'paid' && row.status !== 'waived' && (
                          <button
                            onClick={() => handleMarkPaid(row.arrearsId)}
                            style={{
                              padding: '4px 10px',
                              borderRadius: 6,
                              border: 'none',
                              background: '#16a34a',
                              color: '#fff',
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            Mark Paid
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '16px', borderTop: '1px solid #e2e8f0' }}>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                background: page === 1 ? '#f9fafb' : '#fff',
                color: page === 1 ? '#9ca3af' : '#374151',
                cursor: page === 1 ? 'not-allowed' : 'pointer',
                fontSize: 13,
              }}
            >
              Previous
            </button>
            <span style={{ fontSize: 13, color: '#64748b', alignSelf: 'center' }}>Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                background: page === totalPages ? '#f9fafb' : '#fff',
                color: page === totalPages ? '#9ca3af' : '#374151',
                cursor: page === totalPages ? 'not-allowed' : 'pointer',
                fontSize: 13,
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </RecoveryLayout>
  );
}
